/**
 * OneTopfSimulationEngine.ts
 *
 * Refactored 1-Topf simulation engine extending BaseSimulationEngine
 * Eliminates code duplication while maintaining exact functionality and backward compatibility
 */

import { SteuerParameter } from '@/types';
import { BaseSimulationEngine } from './BaseSimulationEngine';
import {
  CrisisDefinitionType,
  OneTopfInitialValues,
  OneTopfSimulationOptions,
  OneTopfYearData,
  PortfolioStrategy,
  RateCalculationContext,
} from '../types/simulation';

export class OneTopfSimulationEngine extends BaseSimulationEngine<
  OneTopfSimulationOptions,
  OneTopfYearData,
  OneTopfInitialValues
> {
  constructor(
    steuerParameter: SteuerParameter, 
    options: OneTopfSimulationOptions,
    crisisDefinition: CrisisDefinitionType = 'negative-return'
  ) {
    super(steuerParameter, options, crisisDefinition);
  }

  public getStrategy(): PortfolioStrategy {
    return 'one-topf';
  }

  public calculateInitialValues(): OneTopfInitialValues {
    const commonValues = this.calculateCommonInitialValues();

    // For 1-Topf, all money goes into the investment pot
    const totalAmount =
      commonValues.verbrauchstopf + commonValues.zinstopf + commonValues.investitionstopf;

    return {
      investitionstopf: totalAmount,
      total: totalAmount,
    };
  }

  public createYearData(year: number, context: RateCalculationContext): OneTopfYearData {
    const inflationRate = this.getInflationRate(year);
    const investitionstopfRate = this.getInvestitionstopfRate(year);

    // Calculate capital index
    const previousKapitalIndex = year === 0 ? 100 : this.kapitalIndexHistory[year - 1];
    const currentKapitalIndex = this.calculateCurrentKapitalIndex(
      year,
      investitionstopfRate,
      previousKapitalIndex
    );

    // Enhanced crisis detection
    const marktSituation = this.getMarktSituation(investitionstopfRate, year, previousKapitalIndex);

    return {
      jahr: context.age, // Use age (retirement age + year) instead of year index
      alter: context.age,
      investitionstopfRendite: investitionstopfRate,
      inflationsrate: inflationRate,
      investitionstopfSaldo: 0,
      gesamtentnahme: 0,
      zielentnahme: 0, // Set in processYearLogic (inflation-adjusted target)
      gesamtsaldo: 0,
      istKrise: marktSituation === 'Krise',
      marktSituation: marktSituation,
      kapitalIndex: currentKapitalIndex,
      crisisState: this.crisisDetectionService.getCrisisState(),
      entnahmeAktion: '',
      // Additional properties expected by tests - will be populated in processYearLogic
      entnahme: 0,
      steuern: 0,
      nettoentnahme: 0,
      gesamtkapital: 0,
      rendite: investitionstopfRate,
    };
  }

  protected setInitialBalances(
    yearData: OneTopfYearData,
    initialValues: OneTopfInitialValues
  ): void {
    yearData.investitionstopfSaldo = initialValues.investitionstopf;
    yearData.gesamtsaldo = initialValues.total;
    // Set additional properties for initial year
    yearData.gesamtkapital = initialValues.total;
    yearData.entnahme = 0; // Will be calculated in processYearLogic
    yearData.steuern = 0; // Will be calculated in processYearLogic
    yearData.nettoentnahme = 0; // Will be calculated in processYearLogic
  }

  public processYearLogic(yearData: OneTopfYearData, previousYear?: OneTopfYearData): void {
    // Copy previous balance or use initial balance
    let investitionstopfSaldo: number;

    if (!previousYear) {
      // For initial year, use the current balance set by setInitialBalances
      investitionstopfSaldo = yearData.investitionstopfSaldo;
    } else {
      // Copy previous balance
      investitionstopfSaldo = previousYear.investitionstopfSaldo;
    }

    // Calculate inflation-adjusted withdrawal target based on Portfolio-Entnahme
    const baseWithdrawal =
      (this.steuerParameter.vorhandenesKapital * this.steuerParameter.portfolioEntnahmeRate) / 100;
    // Use simulation year index (age - retirement age), not the actual age
    const simulationYear = yearData.alter - this.steuerParameter.retirementAge;
    const inflationAdjustedTarget = this.adjustForInflation(baseWithdrawal, simulationYear);


    // 1-Topf Strategy Logic - Simple direct withdrawal
    let gesamtentnahme = 0;
    let entnahmeAktion = '';

    // TIMING FIX: Withdraw at the START of the year (mirrors 3-Topf timing, which
    // withdraws before interest is applied - including in year 0), THEN apply the
    // investment return only to the remaining balance. Previously the full balance
    // was compounded first, giving the 1-Topf ~withdrawal*r extra return every year.
    const withdrawal = Math.min(investitionstopfSaldo, inflationAdjustedTarget);
    investitionstopfSaldo -= withdrawal;
    gesamtentnahme = withdrawal;

    // Apply investment return to the remaining balance after withdrawal
    investitionstopfSaldo = this.applyInvestmentReturn(
      investitionstopfSaldo,
      yearData.investitionstopfRendite
    );

    if (withdrawal >= inflationAdjustedTarget) {
      entnahmeAktion = `• Investitionstopf-Entnahme: ${Math.round(withdrawal).toLocaleString('de-DE')} €`;
    } else if (withdrawal > 0) {
      entnahmeAktion = `• Partielle Entnahme: ${Math.round(withdrawal).toLocaleString('de-DE')} € (Portfolio erschöpft)`;
    } else {
      entnahmeAktion = '• Keine Entnahme möglich (Portfolio erschöpft)';
    }

    // No tax calculations - using gross withdrawal amounts
    const steuern = 0;
    const nettoentnahme = gesamtentnahme;

    // BUG FIX v2.4.8: Ensure balance never goes negative
    // Cap at zero to prevent negative portfolio values
    investitionstopfSaldo = Math.max(0, investitionstopfSaldo);
    
    // Update year data
    yearData.investitionstopfSaldo = investitionstopfSaldo;
    yearData.gesamtentnahme = gesamtentnahme;
    // Store the inflation-adjusted target withdrawal so the success criterion can compare
    // the actually served withdrawal against the target in the target year.
    yearData.zielentnahme = inflationAdjustedTarget;
    yearData.gesamtsaldo = investitionstopfSaldo; // Same as investitionstopfSaldo for 1-Topf
    yearData.entnahmeAktion = entnahmeAktion;

    // Update additional properties expected by tests
    yearData.entnahme = gesamtentnahme; // Alias for gesamtentnahme
    yearData.steuern = steuern;
    yearData.nettoentnahme = nettoentnahme;
    yearData.gesamtkapital = investitionstopfSaldo; // Alias for gesamtsaldo
    yearData.rendite = yearData.investitionstopfRendite; // Already set in createYearData
  }

  // Legacy method for backward compatibility
  public getInitialValues(): OneTopfInitialValues {
    return this.calculateInitialValues();
  }

  // Legacy method for backward compatibility
  public setSharedRates(rates: {
    inflationRates: number[];
    investitionstopfRates: number[];
  }): void {
    super.setSharedRates({
      inflationRates: rates.inflationRates,
      verbrauchstopfRates: [],
      zinstopfRates: [],
      investitionstopfRates: rates.investitionstopfRates,
    });
  }
}

// Legacy exports for backward compatibility
export type { OneTopfYearData, OneTopfSimulationOptions, OneTopfInitialValues };
